// src/components/Social/SocialSearch.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import './SocialSearch.css';
import API_BASE_URL from '../../config/api';

// ENV compatible (Vite o CRA)
const ENV_API =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) ||
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) ||
  API_BASE_URL;

const ENV_HOST =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_HOST) ||
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_HOST) ||
  ENV_API;

const API_URL = String(ENV_API).replace(/\/+$/, '');
const HOST = String(ENV_HOST).replace(/\/+$/, '');

const DEFAULT_ATHLETE_AVATAR = `${HOST}/uploads/athletes/default.jpg`;
const DEFAULT_TRAINER_AVATAR = `${HOST}/uploads/trainers/default.jpg`;

const sanitizeStored = (raw) => {
  if (raw === undefined || raw === null) return null;
  const v = String(raw).trim();
  if (!v) return null;
  const lower = v.toLowerCase();
  if (lower === 'undefined' || lower === 'null') return null;
  return v;
};

const isObjectId = (v) => /^[a-f\d]{24}$/i.test(String(v || '').trim());

// Mejor resolver (evita agarrar c.user como direct)
const pickUserId = (c) => {
  if (!c) return null;

  const direct = c.userId || c.userID || c._id || c.id || c.uid || null;
  if (direct && isObjectId(direct)) return String(direct);

  const u = c.user || c.otherUser || c.profile || null;
  const nested = u?.userId || u?.userID || u?._id || u?.id || null;
  if (nested && isObjectId(nested)) return String(nested);

  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  if (typeof nested === 'string' && nested.trim()) return nested.trim();

  return null;
};

const resolvePhotoUrl = (raw, { scope } = {}) => {
  if (!raw) return '';
  const v = String(raw).trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith('/')) return `${HOST}${v}`;
  if (scope === 'trainer') return `${HOST}/uploads/trainers/${v}`;
  if (scope === 'athlete') return `${HOST}/uploads/athletes/${v}`;
  return `${HOST}/${v}`;
};

export default function SocialSearch({ asPanel = false, onClose }) {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const isEn = String(language || '').toLowerCase().startsWith('en');
  const copy = {
    partnerFit: isEn ? 'Partner fit' : 'Compatibilidad',
    sharedSports: isEn ? 'Shared sports' : 'Deportes en comun',
    profileNote: isEn ? 'Profile note' : 'Nota del perfil',
    whyConnect: isEn ? 'Why connect' : 'Por que conectar',
    matchReady: isEn ? 'Ready to connect' : 'Listo para conectar',
    sportsCount: isEn ? 'sports in common' : 'deportes en comun',
    quickMatch: isEn
      ? 'Could be useful for coordinating sessions, comparing training zones, or sharing routes.'
      : 'Puede servir para coordinar sesiones, comparar zonas o compartir rutas.',
    noBio: isEn ? 'No profile description yet.' : 'Sin descripcion de perfil todavia.',
  };

  const roleLabel = (role) =>
    role === 'trainer' ? t('common.trainer') : t('common.athlete');

  // userId estable
  const userId = useMemo(() => {
    const a = sanitizeStored(localStorage.getItem('userId'));
    const b = sanitizeStored(localStorage.getItem('userID'));
    const c = sanitizeStored(localStorage.getItem('_id'));

    let picked = null;
    if (isObjectId(a)) picked = a;
    else if (isObjectId(b)) picked = b;
    else if (isObjectId(c)) picked = c;
    else picked = a || b || c;

    // eslint-disable-next-line no-console
    console.log('[SocialSearch] stored ids:', { a, b, c, picked, ok: isObjectId(picked) });

    return isObjectId(picked) ? String(picked) : null;
  }, []);

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const [results, setResults] = useState([]);
  const [explore, setExplore] = useState([]);

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('explore');
  const [error, setError] = useState('');

  // Selected (para Action Sheet)
  const [selected, setSelected] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  // ESC: cierra acción / panel
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (selected) setSelected(null);
        else if (asPanel && onClose) onClose();
        else navigate('/social');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selected, asPanel, onClose, navigate]);

  // debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(timer);
  }, [q]);

  // Carga explorar
  useEffect(() => {
    if (!userId) {
      setError(t('social.invalidSessionUserId'));
      return;
    }

    const loadExplore = async () => {
      try {
        const res = await fetch(`${API_URL}/api/community/recommendations?limit=30`, {
          headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
          cache: 'no-store',
        });

        if (!res.ok) return;

        const data = await res.json();
        const arr = Array.isArray(data) ? data : (data?.results || data?.items || []);

        const mapped = (Array.isArray(arr) ? arr : [])
          .map((c) => {
            const scope = (c.role || c.userRole) === 'trainer' ? 'trainer' : 'athlete';

            const primaryPhoto =
              (Array.isArray(c.photos) && (c.photos[0]?.url || c.photos[0])) ||
              c.photoUrl ||
              c.avatarUrl ||
              c.profilePhoto ||
              (c.user && (c.user.photoUrl || c.user.avatarUrl || c.user.profilePhoto)) ||
              '';

            const id = pickUserId(c);
            const displayName =
              c.displayName ||
              c.name ||
              c.username ||
              (c.user && (c.user.displayName || c.user.name || c.user.username)) ||
              t('social.userFallback');

            return {
              id,
              displayName,
              role: c.role || c.userRole || (c.user && (c.user.role || c.user.userRole)) || 'athlete',
              city: c.city || (c.user && c.user.city) || '',
              country: c.country || (c.user && c.user.country) || '',
              sports: Array.isArray(c.sports)
                ? c.sports
                : c.sports
                ? [c.sports]
                : (Array.isArray(c.user?.sports) ? c.user.sports : c.user?.sports ? [c.user.sports] : []),
              bio: c.bio || c.description || c.about || c.objective || c.goal || c.user?.bio || c.user?.description || c.user?.about || '',
              compatibility: c.compatibility || c.compatibilityScore || c.score || c.matchScore || null,
              photoUrl:
                resolvePhotoUrl(primaryPhoto, { scope }) ||
                (scope === 'trainer' ? DEFAULT_TRAINER_AVATAR : DEFAULT_ATHLETE_AVATAR),
            };
          })
          .filter((x) => !!x.id)
          .filter((x) => String(x.id) !== String(userId));

        setExplore(mapped);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[SocialSearch] loadExplore error', e);
      }
    };

    loadExplore();
  }, [userId, t]);

  // Búsqueda
  useEffect(() => {
    if (!userId) return;

    const query = debouncedQ;
    const isSearching = query.length >= 2;

    setError('');
    setMode(isSearching ? 'search' : 'explore');

    if (!isSearching) {
      setResults([]);
      return;
    }

    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      try {
        const url = `${API_URL}/api/community/search?q=${encodeURIComponent(query)}&limit=30`;
        const res = await fetch(url, {
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
          cache: 'no-store',
        });

        if (res.ok) {
          const data = await res.json();
          const arr = Array.isArray(data) ? data : (data?.results || data?.items || []);

          const mapped = (Array.isArray(arr) ? arr : [])
            .map((c) => {
              const scope = (c.role || c.userRole) === 'trainer' ? 'trainer' : 'athlete';

              const primaryPhoto =
                (Array.isArray(c.photos) && (c.photos[0]?.url || c.photos[0])) ||
                c.photoUrl ||
                c.avatarUrl ||
                c.profilePhoto ||
                (c.user && (c.user.photoUrl || c.user.avatarUrl || c.user.profilePhoto)) ||
                '';

              const id = pickUserId(c);
              const displayName =
                c.displayName ||
                c.name ||
                c.username ||
                (c.user && (c.user.displayName || c.user.name || c.user.username)) ||
                t('social.userFallback');

              return {
                id,
                displayName,
                role: c.role || c.userRole || (c.user && (c.user.role || c.user.userRole)) || 'athlete',
                city: c.city || (c.user && c.user.city) || '',
                country: c.country || (c.user && c.user.country) || '',
                sports: Array.isArray(c.sports)
                  ? c.sports
                  : c.sports
                  ? [c.sports]
                  : (Array.isArray(c.user?.sports) ? c.user.sports : c.user?.sports ? [c.user.sports] : []),
                bio: c.bio || c.description || c.about || c.objective || c.goal || c.user?.bio || c.user?.description || c.user?.about || '',
                compatibility: c.compatibility || c.compatibilityScore || c.score || c.matchScore || null,
                photoUrl:
                  resolvePhotoUrl(primaryPhoto, { scope }) ||
                  (scope === 'trainer' ? DEFAULT_TRAINER_AVATAR : DEFAULT_ATHLETE_AVATAR),
              };
            })
            .filter((x) => !!x.id)
            .filter((x) => String(x.id) !== String(userId));

          setResults(mapped);
          return;
        }

        // fallback local
        const fallback = (explore || []).filter((u) => {
          const hay = `${u.displayName} ${u.city} ${u.country} ${(u.sports || []).join(' ')}`.toLowerCase();
          return hay.includes(query.toLowerCase());
        });
        setResults(fallback);
      } catch (e) {
        if (e?.name !== 'AbortError') setError(t('social.errors.searchFailed'));
      } finally {
        setLoading(false);
      }
    };

    run();
    return () => controller.abort();
  }, [debouncedQ, userId, explore, t]);

  const list = mode === 'search' ? results : explore;

  // Selección → abre ActionSheet
  const onSelectUser = (u) => {
    setActionMsg('');
    setSelected(u);
  };

  // Acción 1: conectar (reutiliza tu like)
  const onConnect = async () => {
    if (!userId || !selected?.id || actionBusy) return;

    setActionBusy(true);
    setActionMsg('');

    try {
      const res = await fetch(`${API_URL}/api/community/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
        body: JSON.stringify({ targetUserId: selected.id, like: true }),
      });

      if (!res.ok) {
        setActionMsg(t('social.errors.connectFailed'));
        return;
      }

      const data = await res.json().catch(() => null);
      setActionMsg(
        data?.isMutual
          ? t('social.matchReviewMessages')
          : t('social.requestSent')
      );

      setExplore((prev) => (prev || []).filter((x) => String(x.id) !== String(selected.id)));
      setResults((prev) => (prev || []).filter((x) => String(x.id) !== String(selected.id)));
    } catch (e) {
      setActionMsg(t('social.errors.sendRequestFailed'));
    } finally {
      setActionBusy(false);
    }
  };

  // Acción 2: bloquear (placeholder UX)
  const onBlock = async () => {
    setActionMsg(t('social.blockPendingEndpoint'));
  };

  // Acción 3: reportar (placeholder UX)
  const onReport = async () => {
    setActionMsg(t('social.reportPendingEndpoint'));
  };

  const close = () => {
    if (selected) {
      setSelected(null);
      return;
    }
    if (asPanel && onClose) onClose();
    else navigate('/social');
  };

  const rootClass = asPanel
    ? 'social-search social-search-root social-search-panel'
    : 'social-search social-search-root';

  return (
    <div className={rootClass}>
      {!asPanel && (
        <button
          type="button"
          className="social-search-fab-close"
          onClick={close}
          aria-label={t('social.closeSearch')}
          title={t('social.close')}
        >
          ✕
        </button>
      )}

      {asPanel && (
        <div className="social-search-panel-header">
          <div className="social-search-panel-title">{t('social.search')}</div>
          <button
            type="button"
            className="social-search-panel-close"
            onClick={close}
            aria-label={t('social.close')}
            title={t('social.close')}
          >
            ✕
          </button>
        </div>
      )}

      <div className="social-search-top">
        <input
          className="social-search-input"
          placeholder={t('social.searchPartnersPlaceholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="social-search-body">
        {mode === 'explore' && (
          <div className="social-search-section-title">{t('social.explore')}</div>
        )}

        {mode === 'search' && (
          <div className="social-search-section-title">
            {t('social.results')} {loading ? `· ${t('social.searching')}` : ''}
          </div>
        )}

        {error && <div className="social-search-error">{error}</div>}

        {!loading && list.length === 0 && (
          <div className="social-search-empty">
            {mode === 'search'
              ? t('social.noResultsFound')
              : t('social.noUsersToExplore')}
          </div>
        )}

        <div className="social-search-list">
          {list.map((u) => (
            <div
              key={String(u.id)}
              className="social-search-item"
              role="button"
              tabIndex={0}
              onClick={() => onSelectUser(u)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onSelectUser(u);
              }}
            >
              <img className="social-search-avatar" src={u.photoUrl} alt={u.displayName} />
              <div className="social-search-meta">
                <div className="social-search-name">
                  {u.displayName}
                  <span className="social-search-role"> · {roleLabel(u.role)}</span>
                </div>
                <div className="social-search-sub">
                  {(u.city || u.country)
                    ? `${u.city}${u.city && u.country ? ', ' : ''}${u.country}`
                    : ' '}
                  {u.sports?.length ? ` · ${u.sports.join(' · ')}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <div className="social-action-overlay" onClick={close}>
          <div className="social-action-sheet" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="social-action-close"
              onClick={close}
              aria-label={t('social.close')}
              title={t('social.close')}
            >
              ×
            </button>
            <div
              className="social-action-cover"
              style={{ backgroundImage: `url(${selected.photoUrl})` }}
              aria-hidden="true"
            />

            <div className="social-action-card">
              <div className="social-action-avatar-wrap">
                <img className="social-action-avatar" src={selected.photoUrl} alt={selected.displayName} />
              </div>

              <div className="social-action-kicker">{copy.partnerFit}</div>
              <div className="social-action-title">{selected.displayName}</div>

              <div className="social-action-fitbar">
                <span>{copy.matchReady}</span>
                <strong>
                  {Number.isFinite(Number(selected.compatibility))
                    ? `${Math.round(Number(selected.compatibility))}%`
                    : `${selected.sports?.length || 0} ${copy.sportsCount}`}
                </strong>
              </div>

              <div className="social-action-meta">
                <span className="social-action-dot">•</span> {roleLabel(selected.role)}
                {(selected.city || selected.country) ? (
                  <>
                    <span className="social-action-dot">•</span>
                    {selected.city}
                    {selected.city && selected.country ? ', ' : ''}
                    {selected.country}
                  </>
                ) : null}
              </div>

              {selected.sports?.length ? (
                <div className="social-action-sports">{selected.sports.join(' · ')}</div>
              ) : null}

              {selected.sports?.length ? (
                <div className="social-action-chiprow">
                  {selected.sports.slice(0, 6).map((sport) => (
                    <span key={sport} className="social-action-chip">{sport}</span>
                  ))}
                </div>
              ) : null}

              <div className="social-action-insight">
                <div>
                  <span className="social-action-insight-label">{copy.profileNote}</span>
                  <p>{selected.bio || copy.noBio}</p>
                </div>
                <div>
                  <span className="social-action-insight-label">{copy.whyConnect}</span>
                  <p>{copy.quickMatch}</p>
                </div>
              </div>

              <div className="social-action-buttons">
                <button
                  type="button"
                  className="social-action-btn"
                  onClick={close}
                  disabled={actionBusy}
                  aria-label={t('social.ignore')}
                  title={t('social.ignore')}
                >
                  <span className="social-action-icon">✕</span>
                  <span className="social-action-label">{t('social.ignore')}</span>
                </button>

                <button
                  type="button"
                  className="social-action-btn social-action-btn-primary"
                  onClick={onConnect}
                  disabled={actionBusy}
                  aria-label={t('social.connect')}
                  title={t('social.connect')}
                >
                  <span className="social-action-icon">❤️</span>
                  <span className="social-action-label social-action-label-strong">
                    {t('social.connect')}
                  </span>
                </button>

                <button
                  type="button"
                  className="social-action-btn"
                  onClick={onBlock}
                  disabled={actionBusy}
                  aria-label={t('social.block')}
                  title={t('social.block')}
                >
                  <span className="social-action-icon">🚫</span>
                  <span className="social-action-label">{t('social.block')}</span>
                </button>

                <button
                  type="button"
                  className="social-action-btn social-action-btn-warn"
                  onClick={onReport}
                  disabled={actionBusy}
                  aria-label={t('social.report')}
                  title={t('social.report')}
                >
                  <span className="social-action-icon">⚠️</span>
                  <span className="social-action-label">{t('social.report')}</span>
                </button>
              </div>

              {actionMsg ? (
                <div className="social-action-toast">
                  <span className="social-action-toast-text">{actionMsg}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
